import { DocumentProps, pdf } from '@react-pdf/renderer';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { LoadingIndicator } from '../../../atoms/LoadingIndicator';
import { pdfStyles } from './pdfStyles';

interface Props {
  document: React.ReactElement<DocumentProps>;
  fileName: string;
  text: string;
  isLoading?: boolean;
}

export const PdfDownloadLinkExtendedV2 = ({ document: docElement, fileName, text, isLoading }: Props) => {
  const [generating, setGenerating] = useState(false);

  const getLoading = useCallback(() => {
    if (isLoading || generating) {
      return (
        <div
          className="loading-indicator"
          style={{ position: 'absolute', width: '100%', height: 36, alignItems: 'center', justifyContent: 'center' }}>
          <div className="flex-center-center-column-nowrap width-100">
            <LoadingIndicator name="dots" size={34} />
          </div>
        </div>
      );
    }

    return null;
  }, [isLoading, generating]);

  const handleDownload = useCallback(async () => {
    try {
      setGenerating(true);

      // Use the `pdf` API to render the provided document element to a blob.
      const asPdf = pdf(docElement);
      // toBlob() is async and will only be invoked on click (not on mount).
      const blob = await asPdf.toBlob();

      // Create an object URL and programmatically download.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke the object URL to free memory.
      URL.revokeObjectURL(url);
    } catch (err) {
      // Keep the error logging simple. You can handle errors more gracefully if needed.
      // eslint-disable-next-line no-console
      console.error('Failed to generate PDF', err);
    } finally {
      setGenerating(false);
    }
  }, [docElement, fileName]);

  return (
    <div className="positionRelative">
      {getLoading()}
      <button type="button" onClick={handleDownload} style={pdfStyles.ionButton}>
        {text}
      </button>
    </div>
  );
};
